import React from 'react';
import { Font, TextInput, TextArea, SelectField, Loader, ButtonRounded } from 'is24-corecss';
import { useForm } from 'react-hook-form';
import type { StorageboxCities, StorageboxData } from '../../../../../../../@types/storagebox';
import { STORAGE_BOX_LOCALIZATION_OPTS } from '../../../../../../../@types/storagebox';
import styles from './styles';
import { useGetUserAccountDetails } from '../../../../../../../apiHooks/userAccount.hook';
import { useIntl } from '@/i18n/hooks/useIntl';
import { default as Msg } from '@/i18n/components/FormattedMessage';

interface Props {
  close: () => void;
  onSubmit: (values: StorageboxData) => Promise<void>;
  isLoading: boolean;
}

const LagerraumForm: React.FC<Props> = ({ close, onSubmit, isLoading }) => {
  const { t } = useIntl();
  const { userAccountDetails } = useGetUserAccountDetails();
  const defaultValues = {
    city: '' as StorageboxCities,
    firstName: userAccountDetails?.firstName || '',
    lastName: userAccountDetails?.lastName || '',
    email: userAccountDetails?.email || '',
    phoneNumber:
      userAccountDetails?.phoneNumber && userAccountDetails?.phoneNumberCountryCode
        ? userAccountDetails.phoneNumber
        : '',
    description: '',
  };

  const {
    register,
    handleSubmit,
    watch,
    formState: { errors, isValid },
  } = useForm({ defaultValues, values: defaultValues, mode: 'all', criteriaMode: 'all' });

  return (
    <form className={styles.container} onSubmit={handleSubmit(onSubmit)} data-testid='LagerraumForm'>
      <div className={styles.content}>
        <div className={styles.column}>
          {isLoading && (
            <div className={styles.loaderContainer} data-testid='loader'>
              <Loader />
            </div>
          )}
          <>
            <div className={styles.row}>
              <SelectField
                id='city'
                {...register('city', {
                  required: true,
                  validate: () => {
                    return STORAGE_BOX_LOCALIZATION_OPTS.includes(watch('city') as StorageboxCities);
                  },
                })}
                label={t({ id: 'benefits.storageService.modal.form.city.label', defaultMessage: 'Gewünschter Ort *' })}
                isErroneous={!!errors.city}
                data-testid='city'
              >
                <option value='' disabled>
                  {t({
                    id: 'benefits.storageService.modal.form.city.placeholder',
                    defaultMessage: '- Standort auswählen -',
                  })}
                </option>
                {STORAGE_BOX_LOCALIZATION_OPTS.map((city) => (
                  <option key={city} value={city}>
                    {city}
                  </option>
                ))}
              </SelectField>
            </div>
            <div className={styles.row}>
              <TextInput.Root id='firstName' invalid={!!errors.firstName}>
                <TextInput.Label>
                  {t({
                    id: 'benefits.storageService.modal.form.firstName.label',
                    defaultMessage: 'Vorname *',
                  })}
                </TextInput.Label>
                <TextInput.Input
                  placeholder=''
                  {...register('firstName', {
                    required: true,
                  })}
                  data-testid='firstName'
                />
              </TextInput.Root>
            </div>
            <div className={styles.row}>
              <TextInput.Root id='lastName' invalid={!!errors.lastName}>
                <TextInput.Label>
                  {t({
                    id: 'benefits.storageService.modal.form.lastName.label',
                    defaultMessage: 'Nachname *',
                  })}
                </TextInput.Label>
                <TextInput.Input
                  {...register('lastName', {
                    required: true,
                  })}
                  placeholder=''
                  data-testid='lastName'
                />
              </TextInput.Root>
            </div>
            <div className={styles.row}>
              <TextInput.Root id='email' invalid={!!errors.email}>
                <TextInput.Label>
                  {t({
                    id: 'benefits.storageService.modal.form.email.label',
                    defaultMessage: 'E-Mail *',
                  })}
                </TextInput.Label>
                <TextInput.Input
                  placeholder=''
                  {...register('email', {
                    required: true,
                  })}
                  data-testid='email'
                />
              </TextInput.Root>
            </div>
            <div className={styles.row}>
              <TextInput.Root id='phoneNumber' invalid={!!errors.phoneNumber}>
                <TextInput.Label>
                  {t({
                    id: 'benefits.storageService.modal.form.phoneNumber.label',
                    defaultMessage: 'Telefonnummer *',
                  })}
                </TextInput.Label>
                <TextInput.Input
                  placeholder=''
                  {...register('phoneNumber', {
                    required: true,
                    maxLength: 17,
                    pattern: {
                      value: /^(\+[1-9]\d{1,3}\s?|\d{1,14})\d{1,14}$/,
                      message: t({
                        id: 'common.errorMessages.invalidPhoneNumber',
                      }),
                    },
                  })}
                  data-testid='phoneNumber'
                />
              </TextInput.Root>
            </div>
            <div className={styles.row}>
              <TextArea
                id='description'
                data-testid='description'
                valid={!errors.description}
                {...register('description')}
                label={t({
                  id: 'benefits.storageService.modal.form.description.label',
                  defaultMessage: 'Nachricht',
                })}
                placeholder={t({
                  id: 'benefits.storageService.modal.form.description.placeholder',
                  defaultMessage: 'Schreibe eine Nachricht....',
                })}
                style={{ resize: 'none' }}
              />
            </div>
          </>
          <div className={styles.disclaimer}>
            <Font className={`${styles.lightgray}`}>
              <Msg
                id='benefits.storageService.modal.form.footerText'
                defaultMessage='Ihre Daten werden zum Zwecke der Kontaktaufnahme und Beratung (E-Mail oder Telefon) an die Lagerbox Holding GmbH, Hauptverwaltung
              Hanauer Landstraße 314, 60314 Frankfurt am Main ("Lagerbox") weitergeleitet.'
              />
            </Font>
          </div>
        </div>
      </div>
      <div className={styles.footer}>
        <div className={styles.submitFooter}>
          <ButtonRounded variant='textDefault' onClick={close} type='button'>
            {t({ id: 'benefits.storageService.modal.form.backButtonText', defaultMessage: 'Zurück' })}
          </ButtonRounded>
          <ButtonRounded type='submit' disabled={isLoading || !isValid}>
            {t({ id: 'benefits.storageService.modal.form.submitButtonText', defaultMessage: 'Anfrage absenden' })}
          </ButtonRounded>
        </div>
      </div>
    </form>
  );
};

export default LagerraumForm;
